import { apiFetch } from './api';

export type WaitlistTier = 'pro' | 'scale';
export type WaitlistSource = 'landing' | 'usage_tab' | 'over_quota_modal';

export interface WaitlistInput {
  email: string;
  tier: WaitlistTier;
  reason?: string;
  source: WaitlistSource;
}

export async function submitWaitlist(input: WaitlistInput): Promise<void> {
  await apiFetch<{ ok: true }>('/api/waitlist', {
    method: 'POST',
    json: input,
  });
}

// Pricing display data — mirrors TIER_LIMITS / PAID_TIERS_DISPLAY on the backend.
// CTAs now point to Stripe Checkout for Pro / Scale; Custom routes to email.
export const PRICING_DISPLAY = {
  free: {
    id: 'free' as const,
    name: 'Free',
    price: 0,
    cta: 'Start free',
    description: 'For trying things out and side projects.',
    features: [
      '20 AI generations / month',
      '100 AI edits / month',
      '50 sends / month',
      'Up to 50 recipients per send',
      '1 sending domain',
      '1 API key',
    ],
  },
  pro: {
    id: 'pro' as const,
    name: 'Pro',
    price: 29,
    cta: 'Upgrade to Pro',
    description: 'For builders shipping newsletters and product updates.',
    features: [
      '500 AI generations / month',
      '5,000 AI edits / month',
      '5,000 sends / month',
      'Up to 100 recipients per send',
      '5 sending domains',
      '5 API keys',
    ],
  },
  scale: {
    id: 'scale' as const,
    name: 'Scale',
    price: 99,
    cta: 'Upgrade to Scale',
    description: 'For teams treating email as a real channel.',
    features: [
      '3,000 AI generations / month',
      '30,000 AI edits / month',
      '50,000 sends / month',
      'Up to 200 recipients per send',
      '20 sending domains',
      '50 API keys',
    ],
  },
  custom: {
    id: 'custom' as const,
    name: 'Custom',
    price: null,
    cta: 'Talk to us',
    description: 'For higher volume, dedicated IPs, or self-hosted deployments.',
    features: [
      'Custom send & generation quotas',
      'Dedicated sending IPs',
      'SSO / SAML',
      'Self-hosted deployment options',
      'White-glove onboarding',
      'Direct Slack support channel',
    ],
  },
} as const;

export type PricingTierId = keyof typeof PRICING_DISPLAY;

/** Public mailto used by the Custom-tier CTA. Keep in sync with Pricing.tsx footer. */
export const SALES_CONTACT_EMAIL = 'hello@sendfast.ai';
export const SALES_CONTACT_MAILTO =
  `mailto:${SALES_CONTACT_EMAIL}?subject=SendFast%20Custom%20plan%20inquiry`;
